import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';

import { CollectionAnimationClub } from '../CollectionAnimationClub';
import { CollectionAnimationHeader } from '../CollectionAnimationHeader';
import type {
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables,
  CollectionAnimationCompetition_anyCardInterface,
  CollectionAnimationCompetition_competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  width: 100%;
  min-width: 100%;
  background-color: var(--c-black);
  position: relative;
  height: 100%;
`;

const CollectionsWrapper = styled(Vertical)`
  transition: transform 1.3s cubic-bezier(0.02, 0.36, 0.34, 0.97);
  flex: 1;
`;

const COLLECTION_ANIMATION_COMPETITION_QUERY = gql`
  query CollectionAnimationCompetitionQuery(
    $user: String!
    $rarities: [Rarity!]
    $competitionSlug: String!
    $seasonStartYear: Int!
  ) {
    currentUser {
      slug
      cardCollections(
        forCompetitions: [$competitionSlug]
        seasonStartYears: [$seasonStartYear]
        rarities: $rarities
        first: 50
      ) {
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
            slots {
              slug
              highlightedCardCollectionCard {
                id
                anyCard {
                  slug
                }
              }
            }
          }
          anyTeam {
            slug
            shortName
          }
          ...CollectionAnimationHeader_cardCollection
          ...CollectionAnimationClub_cardCollection
        }
      }
    }
  }
  ${CollectionAnimationHeader.fragments.cardCollection}
  ${CollectionAnimationClub.fragments.cardCollection}
` as TypedDocumentNode<
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables
>;

type Props = {
  competition: CollectionAnimationCompetition_competition;
  newCards: CollectionAnimationCompetition_anyCardInterface[];
  shouldAnimate: boolean;
  onAnimationEnd: () => void;
  isFirstCompetition: boolean;
};

export const CollectionAnimationCompetition = ({
  competition,
  isFirstCompetition,
  newCards,
  shouldAnimate,
  onAnimationEnd,
}: Props) => {
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const clubsTopRef = useRef<number[]>(null);
  const [translateY, setTranslateY] = useState<number>(0);
  const { data, loading } = useQuery(COLLECTION_ANIMATION_COMPETITION_QUERY, {
    variables: {
      user: contextCurrentUser?.slug || '',
      rarities: [Rarity.common],
      competitionSlug: competition.slug,
      seasonStartYear: newCards.at(0)?.seasonYear as number,
    },
    skip: !contextCurrentUser?.slug || !newCards.at(0)?.seasonYear,
  });
  const cardCollections = data?.currentUser?.cardCollections?.nodes || [];
  const sortedCardCollections = sortBy(
    cardCollection => cardCollection.anyTeam?.shortName || '',
    [...cardCollections]
  );
  const sortedNewCards = sortBy(
    card => card.anyTeam?.shortName || '',
    [...newCards]
  );

  const [animatingCardIndex, setAnimatingCardIndex] = useState<number>();

  const setClubTop = useCallback(
    (ref: HTMLDivElement, index: number) => {
      if (!clubsTopRef.current) {
        clubsTopRef.current = Array.from(
          { length: cardCollections.length },
          () => 0
        );
      }
      clubsTopRef.current[index] = ref.getBoundingClientRect().top || 0;
    },
    [cardCollections.length]
  );

  const getAnimatingCollectionIndex = (_animatingCardIndex: number) => {
    const currentAnimatingCard = sortedNewCards.at(_animatingCardIndex);
    if (!currentAnimatingCard) {
      return undefined;
    }
    return sortedCardCollections.findIndex(cardCollection =>
      cardCollection.userCardCollection.slots.some(
        slot =>
          slot.highlightedCardCollectionCard?.anyCard.slug ===
          currentAnimatingCard.slug
      )
    );
  };

  const getAnimatingCardIndexInCollection = (
    _collection: (typeof sortedCardCollections)[number]
  ) => {
    if (animatingCardIndex === undefined) {
      return undefined;
    }
    const currentAnimatingCard = sortedNewCards.at(animatingCardIndex)!;
    const cardBelongsToCollection = _collection.userCardCollection.slots.some(
      slot =>
        slot.highlightedCardCollectionCard?.anyCard.slug ===
        currentAnimatingCard.slug
    );
    if (!cardBelongsToCollection) {
      return undefined;
    }
    return sortedNewCards
      .slice(0, animatingCardIndex)
      .filter(card =>
        _collection.userCardCollection.slots.some(
          slot => slot.highlightedCardCollectionCard?.anyCard.slug === card.slug
        )
      ).length;
  };

  const onScrollY = (_animatingCardIndex: number) => {
    const collectionIndex = getAnimatingCollectionIndex(_animatingCardIndex);
    if (collectionIndex === undefined) {
      return;
    }
    const firstItemY = clubsTopRef.current?.[0] || 0;
    const itemY = clubsTopRef.current?.[collectionIndex] || 0;
    setTranslateY(firstItemY - itemY);
  };

  const startAnimation = useEffectEvent(() => {
    if (animatingCardIndex === undefined) {
      onScrollY(0);
      setAnimatingCardIndex(0);
    }
  });

  useEffect(() => {
    if (!shouldAnimate || loading) {
      return () => {};
    }
    const timeout = setTimeout(
      () => {
        startAnimation();
      },
      // account for switch between competitions animation
      isFirstCompetition ? 0 : 600
    );
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAnimate, loading]);

  if (loading || cardCollections.length === 0) {
    return null;
  }

  return (
    <Root>
      <CollectionAnimationHeader
        cardCollections={sortedCardCollections}
        competition={competition}
        newCardSlugs={sortedNewCards.map(card => card.slug)}
        animatingCardIndex={animatingCardIndex}
      />
      <CollectionsWrapper
        style={
          {
            transform: `translateY(${translateY}px)`,
          } as CSSProperties
        }
      >
        {sortedCardCollections.map((cardCollection, index) => (
          <CollectionAnimationClub
            key={cardCollection.slug}
            ref={setClubTop}
            index={index}
            cardCollection={cardCollection}
            newCards={sortedNewCards.filter(
              card =>
                isType(card.anyTeam, 'Club') &&
                card.anyTeam?.domesticLeague?.slug === competition.slug
            )}
            animatingCardIndex={getAnimatingCardIndexInCollection(
              cardCollection
            )}
            onCardAnimationEnd={() => {
              if (animatingCardIndex === undefined) {
                return;
              }
              if (animatingCardIndex === sortedNewCards.length - 1) {
                onAnimationEnd();
                return;
              }
              const nextCard = sortedNewCards.at(animatingCardIndex + 1)!;
              const isSameCollection =
                nextCard.anyTeam.slug ===
                sortedNewCards.at(index)?.anyTeam.slug;
              if (!isSameCollection) {
                onScrollY(animatingCardIndex + 1);
              }
              setAnimatingCardIndex(animatingCardIndex + 1);
            }}
          />
        ))}
      </CollectionsWrapper>
    </Root>
  );
};

CollectionAnimationCompetition.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimationCompetition_anyCardInterface on AnyCardInterface {
      slug
      seasonYear
      anyTeam {
        slug
        shortName
        ... on Club {
          slug
          domesticLeague {
            slug
          }
        }
      }
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_anyCardInterface>,
  competition: gql`
    fragment CollectionAnimationCompetition_competition on Competition {
      slug
      ...CollectionAnimationHeader_competition
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_competition>,
};
